import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Phase 12.4B — Native Google OAuth (StUpEco-owned Google Cloud OAuth Client).
 * Consent screen now shows "StUpEco Workspace" (no more Emergent branding).
 * Flow:
 *   1. Click → POST /api/auth/google/authorize?redirect_uri=<origin>/auth/google/callback
 *   2. Redirect the browser to the returned authorize_url.
 *   3. Google → /auth/google/callback?code=…&state=… (handled by AuthCallback.jsx).
 */
export default function GoogleButton({ label = "Continue with Google" }) {
  const [busy, setBusy] = useState(false);

  const handleGoogle = async () => {
    if (busy) return;
    setBusy(true);
    track("google_auth_click");
    try {
      const redirect_uri = window.location.origin + "/auth/google/callback";
      const { data } = await api.post("/auth/google/authorize", { redirect_uri });
      if (!data?.authorize_url) throw new Error("no_authorize_url");
      window.location.href = data.authorize_url;
    } catch (err) {
      setBusy(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start Google sign-in. Please try again.");
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogle}
      disabled={busy}
      data-testid="google-auth-btn"
      className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-sm disabled:opacity-60"
    >
      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      {busy ? "Redirecting to Google…" : label}
    </button>
  );
}
